import { useEffect, useMemo, useRef, useState } from "react";
import { doc, Timestamp } from "firebase/firestore";
import { useWatch } from "react-hook-form";
import { debounce } from "@mui/material";

import { useUserStore } from "@store/user";
import { useDraftStore } from "@store/draft";
import {
  db,
  updateDraftField,
  draftFieldConverter,
  useUpdateFormDraftDataByQuestion,
} from "@api";

type useDraftProps = {
  fieldId: string;
  formId?: string;
};

export type useDraftReturn = {
  handleFocus: () => void;
  handleBlur: () => void;
  isLocked: boolean;
  lockedBy: string | null;
};

export const useDraftField = ({
  fieldId,
  formId,
}: useDraftProps): useDraftReturn | null => {
  const { user } = useUserStore();
  const { setFocusFieldId, clearFocusFieldId } = useDraftStore();
  const fieldStatus = useDraftStore((state) => state.lockedFields[fieldId]);
  const fieldState = useWatch({ name: fieldId });
  const [wasTouched, setWasTouched] = useState(false);

  if (!formId) {
    return null;
  }

  const { mutateFormDraftDataByQuestionAsync } = useUpdateFormDraftDataByQuestion(
    user,
    formId,
  );

  const docReference = useMemo(() => {
    return doc(db, "formInstances", formId, "fields", fieldId).withConverter(
      draftFieldConverter,
    );
  }, [formId, fieldId]);

  const updateFieldValue = useMemo(
    () =>
      debounce((value) => {
        updateDraftField(docReference, {
          value,
        });
      }, 300),
    [docReference],
  );

  useEffect(() => {
    if (fieldState !== undefined) {
      updateFieldValue(fieldState);
    }

    return () => {
      updateFieldValue.clear();
    };
  }, [fieldState, updateFieldValue]);

  const isLocked = useMemo(() => {
    if (!fieldStatus || !fieldStatus.lastTouchedAt || !user?.id) {
      return false;
    }

    const isLockedByAnotherUser = fieldStatus.lockedByUserId !== user.id;
    const isRecentlyTouched =
      (new Date().getTime() - fieldStatus.lastTouchedAt.getTime()) / 1000 < 120;

    return isLockedByAnotherUser && isRecentlyTouched;
  }, [fieldStatus, user?.id]);

  const prevIsLocked = useRef(isLocked);

  useEffect(() => {
    if (prevIsLocked.current && !isLocked) {
      mutateFormDraftDataByQuestionAsync(fieldId);
    }
    prevIsLocked.current = isLocked;
  }, [isLocked]);

  const lockedBy = useMemo(() => {
    if (isLocked && fieldStatus.lockedByUserName) {
      return fieldStatus.lockedByUserName;
    }

    return null;
  }, [isLocked, fieldStatus]);

  const handleFocus = (): void => {
    if (isLocked) return;

    setFocusFieldId(fieldId);
    setWasTouched(true);

    updateDraftField(docReference, {
      isLocked: true,
      lockedByUserId: user?.id,
      lockedByUserName: `${user?.firstName} ${user?.lastName}`,
      lastTouchedAt: Timestamp.fromDate(new Date()),
    });
  };

  const handleBlur = (): void => {
    if (isLocked || !wasTouched) return;

    clearFocusFieldId();

    updateDraftField(docReference, {
      isLocked: false,
    });
  };

  return { handleFocus, handleBlur, isLocked, lockedBy };
};
